
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

const transformToDaily = (data: any[]): DailyEmailData[] => {
  const dailyMap = new Map<string, DailyEmailData>()
  
  data.forEach(record => {
    const date = new Date(record.created_at).toISOString().split('T')[0]
    
    if (!dailyMap.has(date)) {
      dailyMap.set(date, {
        date,
        total_sent: 0,
        total_replies: 0,
        total_bounces: 0,
        hourly_breakdown: []
      })
    }
    
    const dayData = dailyMap.get(date)!
    switch (record.event_type) {
      case 'EMAIL_SENT':
        dayData.total_sent++
        break
      case 'EMAIL_REPLY':
        dayData.total_replies++
        break
      case 'EMAIL_BOUNCE':
        dayData.total_bounces++
        break
    }
  })
  
  return Array.from(dailyMap.values()).sort((a, b) => b.date.localeCompare(a.date))
}

interface DailyEmailData {
  date: string
  total_sent: number
  total_replies: number
  total_bounces: number
  hourly_breakdown: Array<{
    hour: number
    sent: number
    replies: number
    bounces: number
  }>
}

export function useDailyEmailAnalytics(
  timeframe: 'today' | 'last7days' | 'last30days' | 'custom' = 'today',
  customRange?: { from: Date; to: Date }
) {
  const [data, setData] = useState<DailyEmailData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDailyData = async () => {
    try {
      setLoading(true)
      setError(null)

      let startDate: Date
      let endDate: Date = new Date()

      switch (timeframe) {
        case 'today':
          startDate = new Date()
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case 'last7days':
          startDate = new Date()
          startDate.setDate(startDate.getDate() - 7)
          startDate.setHours(0, 0, 0, 0)
          break
        case 'last30days':
          startDate = new Date()
          startDate.setDate(startDate.getDate() - 30)
          startDate.setHours(0, 0, 0, 0)
          break
        case 'custom':
          if (!customRange) return
          startDate = customRange.from
          endDate = customRange.to
          break
        default:
          return
      }

      // For today, get hourly breakdown
      if (timeframe === 'today') {
        const { data: hourlyData, error: hourlyError } = await supabase
          .from('obsync_dynamic_reports')
          .select('created_at, event_type')
          .in('event_type', ['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'])
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: true })

        if (hourlyError) throw hourlyError

        // Process hourly data
        const hourlyBreakdown = Array.from({ length: 24 }, (_, hour) => ({
          hour,
          sent: 0,
          replies: 0,
          bounces: 0
        }))

        hourlyData?.forEach(record => {
          const hour = new Date(record.created_at).getHours()
          switch (record.event_type) {
            case 'EMAIL_SENT':
              hourlyBreakdown[hour].sent++
              break
            case 'EMAIL_REPLY':
              hourlyBreakdown[hour].replies++
              break
            case 'EMAIL_BOUNCE':
              hourlyBreakdown[hour].bounces++
              break
          }
        })

        const todayData: DailyEmailData = {
          date: startDate.toISOString().split('T')[0],
          total_sent: hourlyBreakdown.reduce((sum, h) => sum + h.sent, 0),
          total_replies: hourlyBreakdown.reduce((sum, h) => sum + h.replies, 0),
          total_bounces: hourlyBreakdown.reduce((sum, h) => sum + h.bounces, 0),
          hourly_breakdown: hourlyBreakdown
        }

        setData([todayData])
      } else {
        // For multi-day periods, get daily breakdown from obsync_dynamic_reports
        const { data: dailyData, error: dailyError } = await supabase
          .from('obsync_dynamic_reports')
          .select('created_at, event_type')
          .in('event_type', ['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'])
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: true })

        if (dailyError) throw dailyError

        // Transform to daily breakdown
        const dailyBreakdown = transformToDaily(dailyData || [])
        setData(dailyBreakdown)
      }
    } catch (err) {
      console.error('Error fetching daily email analytics:', err)
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDailyData()

    // Set up real-time subscription for today's data
    if (timeframe === 'today') {
      const channelName = `daily-email-updates-${Math.random().toString(36).substr(2, 9)}`;
      const channel = supabase
        .channel(channelName)
        .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'obsync_dynamic_reports' },
          () => {
            fetchDailyData()
          }
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
  }, []) // Remove dependencies to prevent re-subscription

  return { data, loading, error, refetch: fetchDailyData }
}
